// Portal Dashboard Page
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { POLICY_STATUS_LABELS } from '../lib/policyStatus';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  TrendingUp,
  Users,
  FileCheck,
  AlertTriangle,
  ChevronRight,
  Loader2,
  Calendar,
  XCircle,
  Ban
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { StandardDashboard } from '../components/StandardDashboard';

export function DashboardPage() {
  const { user, activeWorkspaceId, activeWorkspace } = useAuth();
  const [stats, setStats] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [agentSummary, setAgentSummary] = useState(null);

  const isStandard = activeWorkspace?.client_profile === 'full' || activeWorkspace?.type === 'standard';

  const didInitYear = useRef(false);

  useEffect(() => {
    document.title = 'Dashboard · Calyvra Workspace';
    loadData();
    portalApi.getAgentSummary().then(setAgentSummary).catch(() => {});
  }, []);

  // Pick the default coverage year ONCE, only after BOTH the years and the
  // authenticated user are available (avoids a race where an agent falls back
  // to the admin default year 2026 because `user` was still null on mount).
  useEffect(() => {
    if (didInitYear.current) return;
    if (!user || years.length === 0) return;
    didInitYear.current = true;
    let defaultYear;
    if (user.role === 'agent_producer') {
      const activeYears = years.filter(y => y.is_active === true && y.is_archived !== true);
      defaultYear = activeYears.slice().sort((a, b) => (b.year || 0) - (a.year || 0))[0]
        || years.find(y => y.is_default) || years.find(y => y.is_active) || years[0];
    } else {
      defaultYear = years.find(y => y.is_default) || years[0];
    }
    if (defaultYear) setSelectedYear(defaultYear.id);
  }, [user, years]);

  useEffect(() => {
    if (selectedYear !== null && !isStandard) {
      loadStats();
    }
  }, [selectedYear, activeWorkspaceId, isStandard]);

  const loadData = async () => {
    setLoadError(false);
    try {
      const [yearsData] = await Promise.all([
        portalApi.getCoverageYears(),
      ]);
      const accessible = yearsData.filter(y => y.accessible !== false);
      setYears(accessible);
      if (isStandard || accessible.length === 0) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setLoadError(true);
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await portalApi.getDashboardStats(selectedYear, activeWorkspaceId);
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const retryLoad = () => {
    setLoading(true);
    if (selectedYear !== null) {
      loadStats();
    } else {
      loadData();
    }
  };

  const statCards = stats ? [
    { 
      label: 'Total Cases', 
      value: stats.total, 
      icon: FileText, 
      color: 'bg-slate-100 text-slate-600',
      link: '/portal/cases'
    },
    { 
      label: 'New Submissions', 
      value: stats.new, 
      icon: Clock, 
      color: 'bg-sky-100 text-sky-600',
      link: '/portal/cases?status=New+Submission'
    },
    { 
      label: 'Under Review', 
      value: stats.under_review, 
      icon: FileCheck, 
      color: 'bg-amber-100 text-amber-600',
      link: '/portal/cases?status=Under+Review'
    },
    { 
      label: 'Enrolled', 
      value: stats.enrolled, 
      icon: CheckCircle2, 
      color: 'bg-green-100 text-green-600',
      link: '/portal/cases?status=Enrolled'
    },
    { 
      label: 'Needs Attention', 
      value: stats.needs_attention, 
      icon: AlertTriangle, 
      color: 'bg-red-100 text-red-600',
      link: '/portal/cases?needs_attention=true'
    },
    { 
      label: 'Pending Renewal', 
      value: stats.pending_renewal, 
      icon: TrendingUp, 
      color: 'bg-purple-100 text-purple-600',
      link: '/portal/cases?status=Pending+Renewal+Review'
    },
    { 
      label: POLICY_STATUS_LABELS.Terminated, 
      value: stats.policy_terminated, 
      icon: XCircle, 
      color: 'bg-orange-100 text-orange-600',
      link: '/portal/cases?policy_status=Terminated'
    },
    { 
      label: POLICY_STATUS_LABELS.Cancelled, 
      value: stats.policy_cancelled, 
      icon: Ban, 
      color: 'bg-rose-100 text-rose-600',
      link: '/portal/cases?policy_status=Cancelled'
    },
  ] : [];

  const byOffice = stats?.by_office || [];

  const agentCards = agentSummary?.is_agent ? (
    <div className="grid grid-cols-2 gap-4" data-testid="agent-summary">
      <div className="bg-white rounded-xl p-5 border border-slate-200">
        <p className="text-xs text-slate-500 uppercase tracking-wide">My assigned clients</p>
        <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="agent-assigned-count">{agentSummary.assigned_clients}</p>
      </div>
      <div className={`rounded-xl p-5 border ${agentSummary.pending_actions ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
        <p className="text-xs text-slate-500 uppercase tracking-wide">Pending actions</p>
        <p className={`text-3xl font-bold mt-1 ${agentSummary.pending_actions ? 'text-amber-700' : 'text-slate-900'}`} data-testid="agent-pending-count">{agentSummary.pending_actions}</p>
      </div>
    </div>
  ) : null;

  if (isStandard) {
    return (
      <div className="space-y-6">
        {agentCards}
        <StandardDashboard
          workspaceId={activeWorkspaceId}
          selectedYear={selectedYear}
          years={years}
          onYearChange={setSelectedYear}
          userName={user?.name}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {agentCards}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear || ''} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[200px]" data-testid="year-selector">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year.id} value={year.id}>
                  {year.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button asChild data-testid="new-case-btn">
            <Link to={`/portal/cases/new${selectedYear ? `?year=${selectedYear}` : ''}`}>
              New Case
            </Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="dashboard-error">
          <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
          <p className="text-slate-700 font-medium">We couldn't load your dashboard data</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">This is usually a temporary connection issue.</p>
          <Button onClick={retryLoad} data-testid="dashboard-retry-btn">Try again</Button>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <Link
                  key={stat.label}
                  to={stat.link}
                  className="bg-white rounded-xl p-5 border border-slate-200 hover:border-sky-200 hover:shadow-md transition-all"
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`h-10 w-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                </Link>
              );
            })}
          </div>

          {/* Clients per Office Card */}
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="clients-by-office">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Clients per Office</h2>
            {byOffice.length === 0 ? (
              <p className="text-sm text-slate-400">No clients yet for this coverage year.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {byOffice.map((item) => (
                  <Link
                    key={item.office}
                    to={`/portal/cases?office=${encodeURIComponent(item.office)}`}
                    className="text-center p-4 bg-slate-50 rounded-lg hover:bg-sky-50 hover:shadow-sm transition-all"
                    data-testid={`office-stat-${item.office}`}
                  >
                    <p className="text-2xl font-bold text-sky-600">{item.count}</p>
                    <p className="text-sm text-slate-500 mt-1">{item.office}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Link 
                  to={`/portal/cases/new${selectedYear ? `?year=${selectedYear}` : ''}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-sky-500" />
                    <span className="text-slate-700">Create New Case</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
                <Link 
                  to="/portal/cases?status=Documents+Due"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileCheck className="h-5 w-5 text-amber-500" />
                    <span className="text-slate-700">Review Documents Due</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
                <Link 
                  to="/portal/cases?needs_attention=true"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <span className="text-slate-700">Cases Needing Attention</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Status Breakdown</h2>
              <div className="space-y-3">
                {stats && [
                  { label: 'Missing Information', value: stats.missing_information, color: 'bg-amber-500' },
                  { label: 'Documents Due', value: stats.documents_due, color: 'bg-orange-500' },
                  { label: 'Application Submitted', value: stats.application_submitted, color: 'bg-sky-500' },
                  { label: 'Not Eligible', value: stats.not_eligible, color: 'bg-slate-400' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-3 w-3 rounded-full ${item.color}`} />
                      <span className="text-sm text-slate-600">{item.label}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardPage;
